import { ReactNode, useCallback, useMemo, useEffect } from 'react';

import debounce from 'lodash/debounce';
import { FieldProps, useField, FieldRenderProps } from 'react-final-form';

import { RadioInput as Input } from '@vk-hr-tek/ui/input';

import { compose, ValidateType } from '../validation';

/**
 * Компонент радио-кнопок.
 * Оборачивает компонент RadioInput из модуля input в поле формы react-final-form.
 *
 * @public
 * @param label - Подпись для группы радио-кнопок.
 * @param labelIcon - Иконка для метки.
 * @param tooltip - Текст подсказки, отображаемый при наведении на радио-кнопки.
 * @param name - Уникальное имя поля ввода.
 * @param onChange - Пользовательский обработчик изменения значения.
 * @param options - Массив опций для радио-кнопок.
 * @param defaultValue - Значение по умолчанию для поля ввода.
 * @param autoSetDefault - Флаг, указывающий, нужно ли автоматически устанавливать значение по умолчанию.
 * @param required - Флаг, указывающий, является ли поле обязательным.
 * @param disabled - Флаг, указывающий, должно ли поле быть отключено.
 * @param row - Определяет, должны ли радиокнопки отображаться в ряд.
 * @param withoutMargins - Флаг, указывающий, нужно ли убирать отступы.
 * @param validate - Правила валидации.
 * @param debounceTimeout - Таймаут дебаунса для обработчика изменения.
 * @param labelPlacement - Расположение метки относительно радиокнопки.
 * @param showError - Флаг отображения текста ошибки валидации.
 * @param testId - Идентификатор для тестирования.
 * @param rest - Остальные пропсы, передаваемые в {@link useField}.
 * @returns Группу радио-кнопок, обернутую в поле формы.
 */
const RadioInput = ({
  label,
  labelIcon,
  tooltip,
  name,
  onChange,
  options,
  defaultValue,
  autoSetDefault,
  required,
  disabled,
  row,
  withoutMargins = false,
  validate,
  debounceTimeout,
  labelPlacement,
  testId,
  showError = true,
  ...rest
}: {
  defaultValue?: string;
  validate?: (ValidateType | undefined)[] | ValidateType;
  autoSetDefault?: boolean;
  options: {
    value: string | number;
    label: string;
    disabled?: boolean;
    tooltip?: string;
  }[];
  after?: (value: string | undefined) => ReactNode;
  debounceTimeout?: number;
  labelPlacement?: 'start' | 'end' | 'top';
  showError?: boolean;
} & FieldProps<string | undefined, FieldRenderProps<string | undefined>>) => {
  const { input, meta } = useField(name, {
    ...rest,
    validate: Array.isArray(validate) ? compose(validate) : validate,
    initialValue: defaultValue,
  });

  const { onChange: onInputChange } = input;

  const handleChangeForm = useCallback(
    (formValue: string | number) => {
      onInputChange(formValue);

      if (onChange) {
        onChange(formValue);
      }
    },
    [onInputChange, onChange],
  );

  const handleChangeDebounce = useMemo(
    () => debounce(handleChangeForm, debounceTimeout),
    [debounceTimeout, handleChangeForm],
  );

  useEffect(() => {
    return () => {
      handleChangeDebounce.flush();
    };
  }, [handleChangeDebounce]);

  useEffect(() => {
    if (autoSetDefault && !input.value) {
      input.onChange(defaultValue || options[0].value);
    }
  }, [autoSetDefault, defaultValue, input, options]);

  const handleChange = useCallback(
    (changedValue: string | number) => {
      if (debounceTimeout) {
        handleChangeDebounce(changedValue);
      } else {
        handleChangeForm(changedValue);
      }
    },
    [debounceTimeout, handleChangeDebounce, handleChangeForm],
  );

  if (!options || !options.length) {
    return null;
  }

  return (
    <Input
      {...input}
      items={options}
      label={label}
      labelIcon={labelIcon}
      value={input.value}
      onChange={handleChange}
      required={required}
      disabled={disabled}
      tooltip={tooltip}
      row={row}
      withoutMargins={withoutMargins}
      labelPlacement={labelPlacement}
      testId={testId}
      error={meta.touched && !!meta.error}
      helperText={showError && meta.touched && meta.error}
    />
  );
};

export default RadioInput;
